import { create } from 'zustand';
import { User, UserRole } from '../types';
import { authService } from '../services/authService';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { firstName: string; lastName: string; email: string; password: string; role: string; companySlug?: string; invitationToken?: string }) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    set({ token, isAuthenticated: !!token });
  },

  login: async (email: string, password: string) => {
    set({ loading: true });
    try {
      const response = await authService.login({ email, password });
      const { token, user } = response;
      
      localStorage.setItem('token', token);
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      
      set({ 
        token, 
        user, 
        isAuthenticated: true,
        loading: false 
      });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  register: async (data) => {
    set({ loading: true });
    try {
      const response = await authService.register({
        ...data,
        role: data.role as UserRole,
      });
      const { token, user } = response;

      // Only store token and mark as authenticated when a real token is returned.
      // When email verification is required the backend omits the token.
      if (token) {
        localStorage.setItem('token', token);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        set({ token, user, isAuthenticated: true, loading: false });
      } else {
        // Registration succeeded but user must verify email before logging in
        set({ token: null, user: null, isAuthenticated: false, loading: false });
      }
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  fetchCurrentUser: async () => {
    try {
      const user = await authService.getCurrentUser();
      set({ user });
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  },
  
  logout: () => {
    authService.logout();
    set({ user: null, token: null, isAuthenticated: false });
  },
}));
